/**
 * config/layout.ts
 *
 * Breakpoints, pane sizing and route -> page role mapping for the flexible app layout.
 * The layout mode is picked once when the app loads (a browser resize requires a page reload).
 */

import type { RouteLocationNormalizedLoaded } from 'vue-router'

/** Number of side-by-side panes: `one` = phone, `two` = tablet/laptop, `three` = desktop */
export type LayoutMode = 'one' | 'two' | 'three'

/**
 * How a page takes part in the layout:
 * - `folderList`, `folder`, `file`: the panes of the folders browser
 * - `fullscreen`: covers the whole viewport
 * - `dialog`: centered, phone-sized card
 * - `other`: replaces the folder & file panes in the MAIN_VIEW
 */
export type PageRole = 'folderList' | 'folder' | 'file' | 'fullscreen' | 'dialog' | 'other'

/** Lower bound (px) of each breakpoint */
export const LAYOUT_BREAKPOINTS = {
    sm: 600,
    md: 840,
    lg: 1145,
    xl: 1545,
    xxl: 2138,
} as const

/** Width (px) of the app on `xxl` screens, where the layout is not fluid */
export const XXL_APP_WIDTH = 1920

/** FOLDER_LIST column width in the three pane layout */
export const FOLDER_LIST_WIDTH = 'clamp(320px, 22vw, 420px)'

export const SPLIT_PANE = {
    /** Initial share of the container taken by the left pane */
    defaultRatio: { two: 0.5, three: 0.42 },
    /** Left pane cannot get narrower than this (px) */
    leftMin: 320,
    /** Width (px) of the right pane content; a released bar leaving less than half of it collapses the pane */
    rightMin: 360,
} as const

const ROLE_BY_ROUTE_NAME: Record<string, PageRole> = {
    '/folders/': 'folderList',
    '/folders/[folderId]': 'folder',
    '/folders/[folderId].assignments': 'folder',
    '/d/[folderId]/[fileGid]': 'file',
    '/': 'fullscreen',
    '/onboarding': 'fullscreen',
}

export function getPageRole (route: Pick<RouteLocationNormalizedLoaded, 'name'>): PageRole {
    return ROLE_BY_ROUTE_NAME[String(route.name)] ?? 'other'
}

/** Primary input is a finger: tablets and phones */
export function isTouchDevice (): boolean {
    return window.matchMedia('(pointer: coarse)').matches
}

export function detectLayoutMode (width = window.innerWidth, touch = isTouchDevice()): LayoutMode {
    // three panes only make sense with a precise pointer and a lot of room
    if (width >= LAYOUT_BREAKPOINTS.xl && !touch) {
        return 'three'
    }

    if (width >= LAYOUT_BREAKPOINTS.md) {
        return 'two'
    }

    return 'one'
}
